import React, { useEffect, useMemo, useRef, useState } from 'react';
import LockIcon from '@mui/icons-material/Lock';
import Button from '@mui/material/Button';
import { useGridMode } from 'contexts/GridModeContext';
import { getCompletedHandSelectionKey } from 'contexts/HandHistoryContext';
import { useIsActiveTable } from 'contexts/TableActiveContext';
import { useIsTableOnScreen } from 'contexts/TableOnScreenContext';
import { useHandHistory } from 'hooks/useHandHistory';
import { useLazyPanelMount } from 'hooks/useLazyPanelMount';
import { useTableSelector } from 'hooks/useTableSelector';
import { swipeIgnoreProps } from 'utils/swipeIgnore';

import { getCommsPanelStyle } from 'components/CommsPanel/panelGeometry';
import { useDismissOnOutsidePointer } from 'components/CommsPanel/useDismissOnOutsidePointer';

import { ActiveHandCard } from './ActiveHandCard';
import { buildAvatarByName } from './avatar';
import { formatSessionDate } from './format';
import { HAND_CARD_TOGGLE_ATTR, HandCard } from './HandCard';
import { getLiveWinSummaries, LiveWinSummaries } from './liveWinSummary';
import { dealtCards } from './playByPlayRounds';
import { useScrollAnchor, useStableToggle } from './useScrollAnchor';

import styles from './HandHistory.module.scss';

/**
 * Game Chat's sibling. Opens from the neighbouring footer button, paints in the
 * same box (see ./panelGeometry.ts) and follows the same lifecycle:
 *
 * - `useLazyPanelMount`: nothing is built until the first open, then the panel
 *   stays mounted and merely hides, so re-opening is instant and scroll position
 *   survives.
 * - The provider keeps open state per table, while the active/on-screen gates
 *   hide parked panels. A short loading overlay on table switches preserves
 *   the mounted cards and their expansion state underneath.
 *
 * Content is one list: the hand in progress first, then every completed hand of
 * the session, newest first, one card each. One completed card is open at a
 * time; opening another closes the previous one.
 */
const LIVE_CARD_KEY = 'live';
// The context's error can carry raw transport codes (CDN_HTTP_503, socket
// errors); players see one plain message and a way to try again.
const LOAD_ERROR_MESSAGE = 'Could not load hand history.';

export const HandHistoryPanel = () => {
  const {
    data,
    tableScope,
    isPreparing,
    error,
    isLoading,
    refresh,
    isOpen,
    setIsOpen,
    selectedIndex,
    setSelectedIndex
  } = useHandHistory();
  const isGridMode = useGridMode();
  const hasBeenOpened = useLazyPanelMount(isOpen);
  const isActiveTable = useIsActiveTable();
  const isOnScreen = useIsTableOnScreen();
  const tableVisible = isOnScreen && (isGridMode || isActiveTable);
  // The provider now owns a separate open flag for each table. No shared-flag
  // ownership latch is needed (it could reject a restored flag on a parked table).
  const isOpenHere = isOpen && tableVisible;
  const tableVisibleRef = useRef(tableVisible);
  tableVisibleRef.current = tableVisible;
  const [isTransitioning, setIsTransitioning] = useState(Boolean(tableScope && isOpenHere));
  const wasTableVisible = useRef(tableVisible);
  if (wasTableVisible.current !== tableVisible) {
    wasTableVisible.current = tableVisible;
    setIsTransitioning(isOpenHere);
  }
  useEffect(() => {
    if (!isTransitioning) return;
    const timer = window.setTimeout(() => setIsTransitioning(false), 180);
    return () => window.clearTimeout(timer);
  }, [isTransitioning]);
  const showSkeleton = isOpenHere && (isPreparing || isTransitioning);
  const panelRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // One card open at a time, the live one included (Jason, 28 Sep): it opens
  // expanded, and opening any completed hand closes it, and vice versa.
  const [expandedKey, setExpandedKey] = useState<string | null>(LIVE_CARD_KEY);
  const liveExpanded = expandedKey === LIVE_CARD_KEY;
  // A pointer anywhere outside the open panel closes it, like the header's ×.
  useDismissOnOutsidePointer(panelRef, isOpenHere, () => setIsOpen(false));
  const completedHands = data.completedHands;
  // A scrolled reader keeps their place whatever changes above them — the live
  // hand arriving, playing on, clearing or collapsing, a finished hand landing;
  // at the top, the list follows the newest.
  useScrollAnchor(listRef);
  // A tapped header stays under the finger while its hand opens or closes and
  // while the previously open hand collapses (Jason, 28 Sep).
  const holdTappedHeader = useStableToggle(listRef);
  // The live hand's rows borrow the seats' avatars, as the production Table
  // History does; completed hands carry their own in the snapshot.
  const livePlayers = useTableSelector((table) => table.players ?? []);
  const liveTableId = useTableSelector((table) => {
    const gameID = table.game?.gameID;
    return gameID !== undefined && gameID !== null && String(gameID) !== '0'
      ? String(gameID)
      : undefined;
  });
  // Seated players never see a lock: before their first qualifying hand the
  // list simply says where their history will start. Observers who were never
  // seated cannot open the panel at all (the footer trigger is disabled); the
  // lock copy below is the fallback for a panel that is already open when a
  // player stands up before qualifying.
  const isSeated = useTableSelector((table) => Boolean(table.currentPlayer?.isSeated));
  // The viewer's own hole cards for the live card's header and pre-flop round
  // (`p{seat}card1..4` on the table, as the seat itself reads them). Selected
  // as one string so a table tick without a card change re-renders nothing.
  const liveHoleCardKey = useTableSelector((table) => {
    const seatId = table.currentPlayer?.seatId;
    if (seatId === undefined || seatId === null || Number(seatId) < 0) return '';
    const game = (table.game ?? {}) as Record<string, unknown>;
    return dealtCards(
      [1, 2, 3, 4].map((n) => {
        const card = game[`p${seatId}card${n}`];
        return typeof card === 'string' ? card : undefined;
      })
    ).join(',');
  });
  // What the viewer has won so far in the live hand, by the table-scoped seat
  // the reducer resolved from their playerGUID. Selected as one string so a
  // table tick that changes no award re-renders nothing.
  const liveSummaryKey = useTableSelector((table) =>
    JSON.stringify(getLiveWinSummaries(table.game, Number(table.currentPlayer?.seatId)))
  );
  const liveSummaries = useMemo<LiveWinSummaries>(
    () => JSON.parse(liveSummaryKey),
    [liveSummaryKey]
  );
  const liveHoleCards = useMemo(
    () => (liveHoleCardKey ? liveHoleCardKey.split(',') : []),
    [liveHoleCardKey]
  );
  const liveAvatarByName = useMemo(
    () =>
      buildAvatarByName(
        (livePlayers as Array<{ displayName?: string; avatar?: string } | null>).filter(Boolean)
      ),
    [livePlayers]
  );

  // The context's selected hand (also what the playground's example selector
  // and the own-card recovery key off) follows the card the player expands.
  // The reverse direction — a selection made elsewhere expands its card — is
  // only honoured for CHANGES, so the panel still opens with every card
  // collapsed.
  const selectedHand = completedHands[selectedIndex];
  const selectedKey = selectedHand ? getCompletedHandSelectionKey(selectedHand) : undefined;
  const lastSelectionRef = useRef({ index: selectedIndex, key: selectedKey });
  useEffect(() => {
    const previous = lastSelectionRef.current;
    lastSelectionRef.current = { index: selectedIndex, key: selectedKey };
    // Prepending history moves the selected hand's index without a new selection.
    if (previous.index !== selectedIndex && previous.key !== selectedKey && selectedKey) {
      setExpandedKey(selectedKey);
    }
  }, [selectedKey, selectedIndex]);

  const toggleHand = (index: number) => {
    const hand = completedHands[index];
    if (!hand) return;
    const key = getCompletedHandSelectionKey(hand);
    setExpandedKey((current) => (current === key ? null : key));
    setSelectedIndex(index);
  };

  // Focus lands on the first control when the panel shows and returns to the
  // trigger when it hides. Keyed on the table-scoped flag so a hidden panel on
  // another table never steals focus.
  useEffect(() => {
    if (!isOpenHere) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    panelRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    return () => {
      if (tableVisibleRef.current && previousFocus?.isConnected) previousFocus.focus();
    };
  }, [isOpenHere]);

  useEffect(() => {
    if (!isOpenHere) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const panel = panelRef.current;
      if (!(event.target instanceof Node) || !panel?.contains(event.target)) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsOpen(false);
        return;
      }
      if (showSkeleton) return;
      const target = event.target as HTMLElement;
      if (target.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      const toggles = Array.from(
        panel.querySelectorAll<HTMLButtonElement>(`[${HAND_CARD_TOGGLE_ATTR}]`)
      );
      if (toggles.length === 0) return;
      event.preventDefault();
      const currentIndex = toggles.indexOf(document.activeElement as HTMLButtonElement);
      const nextIndex =
        currentIndex === -1
          ? 0
          : event.key === 'ArrowDown'
          ? Math.min(toggles.length - 1, currentIndex + 1)
          : Math.max(0, currentIndex - 1);
      toggles[nextIndex]?.focus();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpenHere, setIsOpen, showSkeleton]);

  // The table and the day, once, in the header: players screenshot this panel
  // for support, and the panel covers the felt's own "TABLE … / HAND …" line.
  const sessionDate = formatSessionDate(completedHands[0]?.completedAt);
  const tableId = liveTableId ?? completedHands[0]?.tableId ?? completedHands[0]?.gameId;

  // Never opened on this table: don't build the panel just to hide it.
  if (!hasBeenOpened) return null;

  return (
    <aside
      ref={panelRef}
      aria-label='Hand History'
      aria-hidden={!isOpenHere}
      className={styles.panel}
      style={getCommsPanelStyle(isGridMode, isOpenHere, { side: 'left' })}
      // Owns its own scroll: with 2+ tables open the trackpad table-swipe
      // Observer preventDefaults every wheel event in its subtree (EN-10881).
      // Chat and the old TableHistory wear the same tag — see utils/swipeIgnore.
      {...swipeIgnoreProps}>
      <header className={styles.panelHeader}>
        <h1>Hand History</h1>
        <span className={styles.panelHeaderMeta}>
          {tableId && (
            <>
              <span className={styles.panelHeaderTable} title='Table number'>
                {tableId}
              </span>
              <span aria-hidden='true'> · </span>
            </>
          )}
          {sessionDate}
        </span>
        <button
          className={styles.closeButton}
          type='button'
          aria-label='Close'
          onClick={() => setIsOpen(false)}>
          <span aria-hidden='true'>&times;</span>
        </button>
      </header>

      {showSkeleton && (
        <div
          className={styles.loadingSkeleton}
          role='status'
          aria-label='Loading hand history'
          aria-busy='true'>
          <span />
          <span />
          <div />
          <span />
          <span />
        </div>
      )}

      {data.locked && !isSeated ? (
        <div className={styles.lockedState}>
          <LockIcon />
          <h2>Take a seat to start your hand history.</h2>
          <p>It starts with the first hand you're dealt at this table.</p>
        </div>
      ) : data.locked ? (
        <div className={styles.handList}>
          <div className={styles.emptyState}>
            <h2>Your history starts with your next hand.</h2>
            <p>Once you're dealt in, every hand you play at this table lands here.</p>
          </div>
        </div>
      ) : (
        <div
          ref={listRef}
          className={styles.handList}
          aria-hidden={showSkeleton || undefined}
          // Hidden from assistive tech under the overlay, so not focusable either.
          {...(showSkeleton ? { inert: '' } : {})}>
          {error && (
            <div className={styles.emptyState} role='alert'>
              <p>{LOAD_ERROR_MESSAGE}</p>
              <Button size='small' variant='outlined' color='inherit' onClick={refresh}>
                Retry
              </Button>
            </div>
          )}

          {data.activeHand && (
            <ActiveHandCard
              hand={data.activeHand}
              avatarByName={liveAvatarByName}
              holeCards={liveHoleCards}
              winSummary={liveSummaries.hand}
              runResults={liveSummaries.byRun}
              expanded={liveExpanded}
              onToggle={(event) => {
                holdTappedHeader(event?.currentTarget);
                setExpandedKey((current) => (current === LIVE_CARD_KEY ? null : LIVE_CARD_KEY));
              }}
            />
          )}

          {completedHands.map((hand, index) => {
            const key = getCompletedHandSelectionKey(hand);
            return (
              <HandCard
                key={key}
                hand={hand}
                expanded={expandedKey === key}
                onToggle={(event) => {
                  holdTappedHeader(event?.currentTarget);
                  toggleHand(index);
                }}
              />
            );
          })}

          {completedHands.length === 0 && !error && (
            <div className={styles.emptyState}>
              <h2>{isLoading ? 'Loading concluded hands...' : 'No completed hands yet.'}</h2>
              <p>Each hand shows up here as soon as the pot is awarded.</p>
            </div>
          )}
        </div>
      )}
    </aside>
  );
};
